/**
 * Review Video Slider — assets/review-video-slider.js
 *
 * Features:
 *  - Lazy-loads video src via IntersectionObserver
 *  - Autoplay / pause based on viewport visibility
 *  - Touch / mouse drag scrolling
 *  - Arrow button navigation
 *  - Dot pagination sync
 *  - Optional auto-scroll (configured via data attribute)
 *  - Keyboard accessible
 */

(function () {
  'use strict';

  /* ── Helpers ────────────────────────────────────────────── */
  const select  = (el, s) => el.querySelector(s);
  const selectAll = (el, s) => [...el.querySelectorAll(s)];

  /* ── Per-section init ───────────────────────────────────── */
  function initSlider(section) {
    const container   = select(section, '[data-rvs-container]');
    const track       = select(section, '[data-rvs-track]');
    const slides      = selectAll(track, '.rvs-slide');
    const prevBtn     = select(section, '[data-rvs-prev]');
    const nextBtn     = select(section, '[data-rvs-next]');
    const dotsWrap    = select(section, '[data-rvs-dots]');
    const dots        = dotsWrap ? selectAll(dotsWrap, '.rvs-dot') : [];
    const videos      = selectAll(track, '.rvs-video');

    if (!container || !track || slides.length === 0) return;

    /* Read section settings injected as data-attributes (set below) */
    const autoScroll      = section.dataset.autoScroll === 'true';
    const autoScrollSpeed = parseInt(section.dataset.autoScrollSpeed || '4', 10) * 1000;

    let currentIndex   = 0;
    let autoScrollTimer = null;
    let isDragging     = false;
    let dragStartX     = 0;
    let dragScrollLeft = 0;

    /* ── 1. Lazy-load videos via IntersectionObserver ──────── */
    const lazyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            loadVideo(video);
            lazyObserver.unobserve(video);
          }
        });
      },
      { root: container, rootMargin: '0px 200px 0px 200px', threshold: 0 }
    );

    videos.forEach((v) => lazyObserver.observe(v));

    function loadVideo(video) {
      if (video.dataset.loaded) return;
      video.dataset.loaded = 'true';

      /* Set <source> src attributes */
      selectAll(video, 'source[data-src]').forEach((s) => {
        s.src = s.dataset.src;
        s.removeAttribute('data-src');
      });

      /* Also set the video's own data-src if present (fallback) */
      if (video.dataset.src) {
        video.src = video.dataset.src;
        video.removeAttribute('data-src');
      }

      video.load();
    }

    /* ── 2. Play / Pause based on viewport ─────────────────── */
    const playObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!video.dataset.loaded) return;
          if (entry.isIntersecting) {
            video.play().catch(() => {}); // swallow autoplay policy errors
          } else {
            video.pause();
          }
        });
      },
      { root: container, threshold: 0.5 }
    );

    videos.forEach((v) => playObserver.observe(v));

    /* ── 3. Scroll helpers ──────────────────────────────────── */
    function getSlideOffset(index) {
      const slide = slides[index];
      if (!slide) return 0;
      return slide.offsetLeft - parseInt(getComputedStyle(track).paddingLeft, 10);
    }

    function scrollTo(index, smooth = true) {
      currentIndex = Math.max(0, Math.min(index, slides.length - 1));
      container.scrollTo({
        left: getSlideOffset(currentIndex),
        behavior: smooth ? 'smooth' : 'instant',
      });
      updateUI();
    }

    function updateUI() {
      /* Dots */
      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === currentIndex);
        d.setAttribute('aria-selected', i === currentIndex ? 'true' : 'false');
      });

      /* Arrows */
      if (prevBtn) {
        prevBtn.disabled = currentIndex === 0;
        prevBtn.setAttribute('aria-disabled', currentIndex === 0);
      }
      if (nextBtn) {
        nextBtn.disabled = currentIndex === slides.length - 1;
        nextBtn.setAttribute('aria-disabled', currentIndex === slides.length - 1);
      }
    }

    /* ── 4. Sync dot/index on native scroll ─────────────────── */
    let scrollRAF = null;
    container.addEventListener('scroll', () => {
      cancelAnimationFrame(scrollRAF);
      scrollRAF = requestAnimationFrame(syncFromScroll);
    }, { passive: true });

    function syncFromScroll() {
      const scrollLeft = container.scrollLeft;
      let closest = 0;
      let minDist = Infinity;
      slides.forEach((slide, i) => {
        const dist = Math.abs(slide.offsetLeft - parseInt(getComputedStyle(track).paddingLeft, 10) - scrollLeft);
        if (dist < minDist) { minDist = dist; closest = i; }
      });
      if (closest !== currentIndex) {
        currentIndex = closest;
        updateUI();
      }
    }

    /* ── 5. Arrow buttons ───────────────────────────────────── */
    if (prevBtn) prevBtn.addEventListener('click', () => scrollTo(currentIndex - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollTo(currentIndex + 1));

    /* ── 6. Dots ────────────────────────────────────────────── */
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => scrollTo(i));
    });

    /* ── 7. Keyboard navigation on container ───────────────── */
    container.setAttribute('tabindex', '0');
    container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); scrollTo(currentIndex + 1); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); scrollTo(currentIndex - 1); }
      if (e.key === 'Home')       { e.preventDefault(); scrollTo(0); }
      if (e.key === 'End')        { e.preventDefault(); scrollTo(slides.length - 1); }
    });

    /* ── 8. Mouse drag ──────────────────────────────────────── */
    container.addEventListener('mousedown', (e) => {
      isDragging    = true;
      dragStartX    = e.pageX - container.offsetLeft;
      dragScrollLeft = container.scrollLeft;
      container.style.cursor = 'grabbing';
    });

    window.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      container.style.cursor = '';
      syncFromScroll();
    });

    container.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const x    = e.pageX - container.offsetLeft;
      const walk = (x - dragStartX) * 1.2;
      container.scrollLeft = dragScrollLeft - walk;
    });

    /* Prevent click-through after drag */
    container.addEventListener('click', (e) => {
      if (Math.abs(container.scrollLeft - dragScrollLeft) > 4) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    /* ── 9. Auto-scroll ─────────────────────────────────────── */
    function startAutoScroll() {
      if (!autoScroll) return;
      autoScrollTimer = setInterval(() => {
        const next = (currentIndex + 1) % slides.length;
        scrollTo(next);
      }, autoScrollSpeed);
    }

    function stopAutoScroll() {
      clearInterval(autoScrollTimer);
    }

    /* Pause auto-scroll on user interaction */
    ['mousedown', 'touchstart', 'keydown'].forEach((evt) => {
      container.addEventListener(evt, stopAutoScroll, { passive: true });
    });

    /* Resume after mouse leaves section */
    section.addEventListener('mouseleave', startAutoScroll);

    startAutoScroll();

    /* ── Initial UI state ───────────────────────────────────── */
    updateUI();
  }

  /* ── Boot ───────────────────────────────────────────────── */
  function boot() {
    document.querySelectorAll('[data-section-id]').forEach((section) => {
      /* Only target our sections (they have [data-rvs-track]) */
      if (section.querySelector('[data-rvs-track]')) {
        initSlider(section);
      }
    });
  }

  /* Support Shopify theme editor live reloading */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Shopify section:load event (theme editor) */
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target;
    if (section && section.querySelector('[data-rvs-track]')) {
      initSlider(section);
    }
  });

})();